import {
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableRow,
  TableHead,
  IconButton,
  Box,
  Skeleton,
  Alert,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from "@mui/icons-material";
import CustomPagination from "./customPagiation";
import { PropsWithChildren } from "react";

const StyledTableCell = styled(TableCell)(() => ({}));

/**
 * A row.
 *
 * Zebra striping used to shade every odd row, which on a page of five rows
 * reads as three selected ones. Rows are separated by a hairline and lift on
 * hover instead - the reader follows the row under the cursor rather than the
 * one the stripe happened to land on.
 */
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "background-color .12s ease",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPage />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPage />
      </IconButton>
    </Box>
  );
}

interface DataTableProps {
  tableOptions: any;
  rows: any[];
  ActionChildren: any;
  handleChangeRowsPerPage?: (event: any) => void;
  page?: number;
  handleChangePage?: (event: any, newPage: number) => void;
  rowsPerPage?: number;
  totalCount?: number;
  loading: boolean;
  error: boolean;
  onSelectAllClick?: (event: any) => void;
  selectedItems?: string[];
  onSelectItemClick?: (event: any) => void;
  setOrderType?: (orderType: "asc" | "desc") => void;
  orderType?: "asc" | "desc";
  customRowsPerPageOptions?: number[];
  disablePagination?: boolean;
  emptyStateText?: string;
  style?: React.CSSProperties;
  children?: any;
}

export default function CustomDataTable({
  tableOptions,
  rows = [],
  ActionChildren,
  handleChangeRowsPerPage,
  page,
  handleChangePage,
  rowsPerPage,
  totalCount,
  loading,
  error,
  onSelectAllClick,
  selectedItems,
  onSelectItemClick,
  setOrderType,
  orderType,
  customRowsPerPageOptions,
  disablePagination,
  emptyStateText,
  style,
  children,
}: DataTableProps) {
  const skeletonLoading = (headItems) => {
    let content = [];
    for (let i = 0; i < 5; i++) {
      content.push(
        <StyledTableRow key={"skeleton-loader-row-" + i}>
          {headItems.map((item) => (
            <TableCell
              key={"row-item-skeleton-" + (item.key || item.id)}
              component="th"
              scope="row"
              align={item.align}
            >
              <Skeleton animation="wave" />
            </TableCell>
          ))}
          {tableOptions.actionStatus && (
            <TableCell
              key={"row-item-skeleton-actionbar-" + i}
              component="th"
              scope="row"
              align={"center"}
            >
              <Skeleton animation="wave" />
            </TableCell>
          )}
        </StyledTableRow>
      );
    }
    return content;
  };

  return (
    <TableContainer sx={{ marginBottom: 2, ...style }}>
      {error && !loading && (
        <Alert severity="error">
          <span>Unexpected Error Occured</span>
        </Alert>
      )}
      <Table
        sx={{ minWidth: 500 }}
        aria-label="custom pagination table"
      >
        <TableHead>
          {error ? (
            <TableRow color={"red"}>
              <StyledTableCell align={"center"}>{tableOptions.title}</StyledTableCell>
            </TableRow>
          ) : (
            <TableRow color={"red"}>
              {tableOptions.selectedRow && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    //indeterminate={true}
                    checked={selectedItems.includes("all")}
                    onChange={onSelectAllClick}
                    inputProps={{
                      "aria-label": "select all desserts",
                    }}
                  />
                </TableCell>
              )}
              {tableOptions.tableHeadItems.map((item) =>
                item.sortEvent ? (
                  <StyledTableCell
                    align={item.align}
                    key={"table-head-item-" + (item.key || item.id)}
                  >
                    <TableSortLabel
                      onClick={() => setOrderType(orderType === "asc" ? "desc" : "asc")}
                      direction={orderType}
                      active={true}
                    >
                      {item.name}
                    </TableSortLabel>
                  </StyledTableCell>
                ) : (
                  <StyledTableCell
                    align={item.align}
                    key={"table-head-item-" + (item.key || item.id)}
                  >
                    {item.name}
                  </StyledTableCell>
                )
              )}

              {tableOptions.actionStatus && (
                <StyledTableCell align="right">Actions</StyledTableCell>
              )}
            </TableRow>
          )}
        </TableHead>

        <TableBody>
          {loading &&
            skeletonLoading(
              tableOptions.isSelected
                ? [...tableOptions.tableHeadItems, { align: "center" }]
                : tableOptions.tableHeadItems
            )}
          {rows.length == 0 && !loading && (
            <StyledTableRow sx={{ "&:hover": { backgroundColor: "transparent" } }}>
              <TableCell
                sx={{ textAlign: "center", py: 6, color: "text.secondary" }}
                colSpan={tableOptions.tableHeadItems.length + 1}
              >
                {emptyStateText || "Nothing to show yet."}
              </TableCell>
            </StyledTableRow>
          )}
          {rows.length > 0 &&
            !error &&
            !loading &&
            rows.map((row) => (
              <StyledTableRow key={"table-row-" + (row.id || row.key)}>
                {tableOptions.selectedRow && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      disabled={selectedItems.includes("all")}
                      checked={
                        selectedItems.includes(row.id.toString()) || selectedItems.includes("all")
                      }
                      onChange={onSelectItemClick}
                      inputProps={{
                        "aria-label": "select item desserts",
                        id: row.id.toString(),
                      }}
                    />
                  </TableCell>
                )}
                {tableOptions.tableHeadItems.map((item) => {
                  let currentRow = null;

                  if (children && children[item.key]) {
                    currentRow = children[item.key];
                  }
                  return (
                    <TableCell
                      component="th"
                      scope="row"
                      align={item.align}
                      key={"row-item-" + (item.key || item.id)}
                      sx={item.styled}
                    >
                      {children && children[item.key] ? children[item.key]({ row }) : row[item.key]}
                    </TableCell>
                  );
                })}
                {tableOptions.actionStatus && (
                  <TableCell
                    component="th"
                    scope="row"
                    align="right"
                  >
                    <ActionChildren row={row} />
                  </TableCell>
                )}
              </StyledTableRow>
            ))}
        </TableBody>
        {!disablePagination && (
          <TableFooter sx={{ borderTop: "1px solid #e0e0e0" }}>
            <TableRow>
              {rows.length >= 1 && !error && !loading && (
                <CustomPagination
                  rowsPerPageOptions={
                    customRowsPerPageOptions ? customRowsPerPageOptions : [5, 10, 25]
                  }
                  tableOptions={tableOptions}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  totalCount={totalCount}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  TablePaginationActions={TablePaginationActions}
                />
              )}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
